import { ElementRef } from '@angular/core';
import { SortableStopEvent } from '@shopify/draggable';
import { isEqual } from 'lodash';

export interface RvSortableItem {
  rvSortableId?: string;
  [key: string]: any;
}

export interface RvSortableConfig<T extends RvSortableItem> {
  items: T[];
  getDisplayName: (item: T) => string;
  onReorder: (items: T[]) => void;
  afterPositionChanged?: (items: T[]) => void;
  hostElement: ElementRef<HTMLElement>;
  appendTo?: string;
}

export class RvSortableService<T extends RvSortableItem> {
  private selectedItem: T | null = null;
  private preSortItemsOrder: string[] = [];
  public isKeyboardMoving: boolean = false;

  constructor(public config: RvSortableConfig<T>) {
    // Ensure all items have unique IDs
    this.ensureItemsHaveIds();
  }

  /**
   * Ensures all items have unique rvSortableId properties.
   * If an item doesn't have one, generates it automatically.
   */
  private ensureItemsHaveIds(): void {
    const usedIds = new Set<string>();

    this.config.items.forEach((item, index) => {
      if (typeof item === 'object' && item !== null) {
        if (!item.rvSortableId || usedIds.has(item.rvSortableId)) {
          item.rvSortableId = this.generateUniqueId(index);
        }

        usedIds.add(item.rvSortableId);
      }
    });
  }

  /**
   * Generates a unique ID for an item
   */
  private generateUniqueId(index: number): string {
    return `rv-sortable-item-${Date.now()}-${index}-${Math.random().toString(36).substring(2, 11)}`;
  }

  /**
   * Call this method when items are added or the items array is replaced
   * to ensure new items have IDs
   */
  public updateItems(items: T[]): void {
    this.config.items = items;
    this.ensureItemsHaveIds();
  }

  get selectedIndex(): number | null {
    return this.selectedItem
      ? this.config.items.findIndex(
          (item) => item.rvSortableId === this.selectedItem!.rvSortableId,
        )
      : null;
  }

  onDragEnd(sortableEvent?: SortableStopEvent): void {
    if (sortableEvent) {
      this.moveItem(sortableEvent.oldIndex, sortableEvent.newIndex);
      this.config.onReorder(this.config.items);
    }
  }

  handleSortableKeydown(event: KeyboardEvent, item: T): void {
    switch (event.key) {
      case ' ':
      case 'Enter':
        event.preventDefault();
        if (this.selectedItem === null) {
          this.selectItem(item);
        } else {
          this.reset();
          if (
            !isEqual(
              this.preSortItemsOrder,
              this.config.items.map((i) => i.rvSortableId!),
            )
          ) {
            this.commitKeyboardSort();
          }
        }
        break;

      case 'ArrowUp':
        event.preventDefault();
        const currentIndex = this.selectedIndex;
        if (currentIndex !== null && currentIndex > 0) {
          this.isKeyboardMoving = true;
          this.moveItem(currentIndex, currentIndex - 1);
          this.focusHandle();
          setTimeout(() => (this.isKeyboardMoving = false), 100);
        }
        break;

      case 'ArrowDown':
        event.preventDefault();
        const currentIndex2 = this.selectedIndex;
        if (
          currentIndex2 !== null &&
          currentIndex2 < this.config.items.length - 1
        ) {
          this.isKeyboardMoving = true;
          this.moveItem(currentIndex2, currentIndex2 + 1);
          this.focusHandle();
          setTimeout(() => (this.isKeyboardMoving = false), 100);
        }
        break;

      case 'Escape':
        if (this.selectedItem !== null) {
          event.preventDefault();
          this.revertKeyboardSort();
        }
        break;

      case 'Tab':
        if (this.selectedItem !== null) {
          event.preventDefault();
        }
        break;
    }
  }

  private selectItem(item: T): void {
    this.selectedItem = item;
    this.preSortItemsOrder = this.config.items.map((i) => i.rvSortableId!);
    this.updateItemAriaLabel(true);
    this.toggleDraggableMirrorClass(true);
  }

  private moveItem(fromIndex: number, toIndex: number): void {
    const items = this.config.items;
    const item = items[fromIndex];
    items.splice(fromIndex, 1);
    items.splice(toIndex, 0, item);
    this.config.afterPositionChanged?.(items);
  }

  private commitKeyboardSort(): void {
    this.config.onReorder([...this.config.items]);
  }

  revertKeyboardSort(): void {
    if (
      this.selectedItem === null ||
      !this.preSortItemsOrder?.length ||
      this.isKeyboardMoving
    ) {
      return;
    }

    setTimeout(() => {
      const preSortItemsOrder = [...this.preSortItemsOrder];
      this.reset();

      const items = this.config.items;
      const itemsById = new Map(
        items.map((item) => [item.rvSortableId!, item]),
      );

      // Mutate in place to preserve the array reference
      items.length = 0;
      for (const id of preSortItemsOrder) {
        const item = itemsById.get(id);
        if (item) items.push(item);
      }
    });
  }

  private updateItemAriaLabel(isSelected: boolean): void {
    if (!this.selectedItem) return;

    const handle = this.config.hostElement.nativeElement.querySelector(
      `[data-item-id="${this.selectedItem.rvSortableId}"]`,
    ) as HTMLElement | null;

    if (handle) {
      const baseLabel = `Move ${this.config.getDisplayName(this.selectedItem)}`;
      handle.setAttribute(
        'aria-label',
        isSelected
          ? `${baseLabel}. Selected. Use arrow keys to move, Space to drop, Escape to cancel.`
          : `${baseLabel}. Press space to select, then use arrow keys to reorder.`,
      );
    }
  }

  private focusHandle(): void {
    setTimeout(() => {
      if (!this.selectedItem) {
        return;
      }

      const handle = this.config.hostElement.nativeElement.querySelector(
        `[data-item-id="${this.selectedItem.rvSortableId}"]`,
      ) as HTMLElement | null;
      handle?.focus();
    });
  }

  reset(): void {
    if (this.selectedItem !== null) {
      this.toggleDraggableMirrorClass(false);
      this.updateItemAriaLabel(false);
    }
    this.selectedItem = null;
    this.preSortItemsOrder = [];
    this.isKeyboardMoving = false;
  }

  private toggleDraggableMirrorClass(selected: boolean): void {
    const items =
      this.config.hostElement.nativeElement.querySelectorAll(
        '.rv-sortable-item',
      );
    const item = items[this.selectedIndex] as HTMLElement | null;
    if (item) {
      if (selected) {
        item.classList.add('keyboard-selected');
      } else {
        item.classList.remove('keyboard-selected');
      }
    }
  }
}
